/**
 * React Query hooks for chat functionality
 * Provides hooks for querying Vertex AI with building code questions
 */

import { useMutation } from '@tanstack/react-query';
import { vertexAIProxyClient } from '@/api/clients/vertex-ai-proxy-client';
import type { RagResponse } from '@/types/chat';

/**
 * Hook to query Vertex AI with a building code question
 * Uses mutation since each query is a unique operation
 */
export function useVertexAIQuery() {
  return useMutation<RagResponse, Error, string>({
    mutationFn: async (prompt: string) => {
      return await vertexAIProxyClient.query(prompt);
    },
    onError: (error) => {
      console.error('[Chat Hook] Vertex AI query failed:', error);
    },
    onSuccess: (data) => {
      console.log('[Chat Hook] Query successful, response length:', data.regulationText.length);
    },
  });
}

/**
 * Hook to query with context (BIM model information)
 * Enhances the prompt with current model context
 */
export interface QueryWithContextParams {
  prompt: string;
  context?: {
    modelUrn?: string;
    selectedElements?: number[];
    extractedData?: Record<string, unknown>;
  };
}

export function useVertexAIQueryWithContext() {
  return useMutation<RagResponse, Error, QueryWithContextParams>({
    mutationFn: async ({ prompt, context }) => {
      let enhancedPrompt = prompt;

      // Add context information to the prompt if available
      if (context) {
        const contextParts: string[] = [];

        if (context.modelUrn) {
          contextParts.push(`Context: User is viewing BIM model ${context.modelUrn}`);
        }

        if (context.selectedElements && context.selectedElements.length > 0) {
          contextParts.push(`Selected elements: ${context.selectedElements.length} element(s)`);
        }

        if (context.extractedData) {
          const dataKeys = Object.keys(context.extractedData as Record<string, unknown>);
          if (dataKeys.length > 0) {
            contextParts.push(`Available data: ${dataKeys.join(', ')}`);
          }
        }

        if (contextParts.length > 0) {
          enhancedPrompt = `${contextParts.join('\n')}\n\nQuestion: ${prompt}`;
        }
      }

      return await vertexAIProxyClient.query(enhancedPrompt);
    },
    onError: (error) => {
      console.error('[Chat Hook] Context query failed:', error);
    },
  });
}
